import { DataProvider, fetchUtils } from 'react-admin';
import { API_BASE } from '../utils/apiClient';

const apiUrl = `${API_BASE}/admin/api`;

const httpClient = (url: string, options: fetchUtils.Options = {}) => {
  const customOptions: fetchUtils.Options = {
    ...options,
    credentials: 'include' as RequestCredentials,
  };
  return fetchUtils.fetchJson(url, customOptions);
};

export const dataProvider: DataProvider = {
  /**
   * Get a list of resources
   * Uses ra-data-simple-rest compatible format with range/sort/filter as JSON
   */
  getList: async (resource, params) => {
    const { page, perPage } = params.pagination;
    const { field, order } = params.sort;

    // Calculate range for pagination [start, end]
    const rangeStart = (page - 1) * perPage;
    const rangeEnd = page * perPage - 1;

    const query = new URLSearchParams({
      range: JSON.stringify([rangeStart, rangeEnd]),
      sort: JSON.stringify([field, order]),
      filter: JSON.stringify(params.filter),
    });

    const url = `${apiUrl}/${resource}?${query.toString()}`;
    const { json, headers } = await httpClient(url);

    // Parse Content-Range header for total count
    const contentRange = headers.get('Content-Range');
    let total = 0;
    if (contentRange) {
      const match = contentRange.match(/\/(\d+)$/);
      if (match) {
        total = parseInt(match[1], 10);
      }
    }

    return {
      data: json,
      total: total || json.length,
    };
  },

  /**
   * Get a single resource by ID
   */
  getOne: async (resource, params) => {
    const url = `${apiUrl}/${resource}/${params.id}`;
    const { json } = await httpClient(url);
    return { data: json };
  },

  /**
   * Get multiple resources by IDs
   */
  getMany: async (resource, params) => {
    const query = new URLSearchParams({
      filter: JSON.stringify({ id: params.ids }),
    });

    const url = `${apiUrl}/${resource}?${query.toString()}`;
    const { json } = await httpClient(url);
    return { data: json };
  },

  /**
   * Get resources referenced by another resource
   */
  getManyReference: async (resource, params) => {
    const { page, perPage } = params.pagination;
    const { field, order } = params.sort;

    const rangeStart = (page - 1) * perPage;
    const rangeEnd = page * perPage - 1;

    const query = new URLSearchParams({
      range: JSON.stringify([rangeStart, rangeEnd]),
      sort: JSON.stringify([field, order]),
      filter: JSON.stringify({
        ...params.filter,
        [params.target]: params.id,
      }),
    });

    const url = `${apiUrl}/${resource}?${query.toString()}`;
    const { json, headers } = await httpClient(url);

    const contentRange = headers.get('Content-Range');
    let total = 0;
    if (contentRange) {
      const match = contentRange.match(/\/(\d+)$/);
      if (match) {
        total = parseInt(match[1], 10);
      }
    }

    return {
      data: json,
      total: total || json.length,
    };
  },

  /**
   * Create a new resource
   */
  create: async (resource, params) => {
    const url = `${apiUrl}/${resource}`;
    const { json } = await httpClient(url, {
      method: 'POST',
      body: JSON.stringify(params.data),
    });
    return { data: json };
  },

  /**
   * Update a resource
   */
  update: async (resource, params) => {
    const url = `${apiUrl}/${resource}/${params.id}`;
    const { json } = await httpClient(url, {
      method: 'PUT',
      body: JSON.stringify(params.data),
    });
    return { data: json };
  },

  /**
   * Update multiple resources
   */
  updateMany: async (resource, params) => {
    const responses = await Promise.all(
      params.ids.map((id) =>
        httpClient(`${apiUrl}/${resource}/${id}`, {
          method: 'PUT',
          body: JSON.stringify(params.data),
        })
      )
    );
    return { data: responses.map(({ json }) => json.id) };
  },

  /**
   * Delete a resource
   */
  delete: async (resource, params) => {
    const url = `${apiUrl}/${resource}/${params.id}`;
    const { json } = await httpClient(url, {
      method: 'DELETE',
    });
    return { data: json };
  },

  /**
   * Delete multiple resources
   */
  deleteMany: async (resource, params) => {
    await Promise.all(
      params.ids.map((id) =>
        httpClient(`${apiUrl}/${resource}/${id}`, {
          method: 'DELETE',
        })
      )
    );
    return { data: params.ids };
  },
};
